import { faArrowRight } from "@fortawesome/pro-solid-svg-icons/faArrowRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, FormHelperText, Grid, IconButton, RadioGroup, Stack, TextField, Typography } from "@mui/material";
import { MprcContainer, MprcFormSubHeader, MprcStringToHtml } from "components";
import { IconCopy } from "components/icons/IconCopy";
import { DatePositionChanged, MprcDateTimePicker, MprcDateTimeSource } from "components/MprcDateTimePicker";
import { MprcRadio } from "components/MprcRadio";
import { MprcTimezoneSelect } from "components/MprcTimezoneSelect";
import { AddressModel, ManagedMeetingDto, ManagedMeetingLocationDto, ManagedMeetingOnlineLocationDto, MeetingRoomAutoCompleteDataModelDto, MeetingType } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import HtmlUtils from "lib/HtmlUtils";
import LibUtils from "lib/LibUtils";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MeetingRoomAutoComplete } from "views/lms/settings/Home/MeetingRoom/components/MeetingRoomAutoComplete";
import { buildEndDate, EditMeetingMode } from "./EditMeeting";
import { EditMeetingAddressBox, getEmptyAddressModel } from "./EditMeetingAddressBox";

////////////////////////////////////////////////////////////////////////////////

interface IData {
    startDate: Date | null;
    endDate: Date | null;
    meetingType: MeetingType;
    multipleClasses: boolean;
    meetingRoom: MeetingRoomAutoCompleteDataModelDto | null;
    location: ManagedMeetingLocationDto | null;
    onlineLocation: ManagedMeetingOnlineLocationDto | null;
    meetingTypesEnabled: MeetingType[];
}

interface IPros {
    mode: EditMeetingMode;
    visible: boolean;
    viewData: IData;
    viewErrors: ApiProblemErrors | undefined;
    readonly?: boolean;

    onFormUpdate: (dto: Partial<ManagedMeetingDto>) => void;
    onFormValidation: (valid: boolean, hasError: boolean) => void;
    onError: (error: any) => void;
}

interface IValidationFields {
    startDate: boolean;
    endDate: boolean;
    timezoneId: boolean;
    meetingRoomId: boolean;
    externalUrl: boolean;
    locationName: boolean;
}
interface IPostValidation {
    startDate: string[] | undefined;
    endDate: string[] | undefined;
    timezoneId: string[] | undefined;
    meetingRoomId: string[] | undefined;
    externalUrl: string[] | undefined;
    locationName: string[] | undefined;
}

////////////////////////////////////////////////////////////////////////////////

function getDefaultValidate(dto: IData, mode: EditMeetingMode): IValidationFields {
    const locationValid = dto.meetingType != MeetingType.Presential || dto.meetingRoom != null || (dto.multipleClasses && mode == "training") || (dto.location?.locationName != null && dto.location.locationName.length > 0);

    return {
        startDate: (dto.multipleClasses && mode == "training" && dto.startDate == null) || dto.startDate != null,
        endDate: (dto.multipleClasses && mode == "training" && dto.startDate == null && dto.endDate == null) || (dto.startDate != null && dto.endDate != null && dto.endDate >= dto.startDate),
        timezoneId: dto.meetingRoom != null || (dto.meetingType == MeetingType.Presential && dto.location?.timezoneId != null) || (dto.meetingType != MeetingType.Presential && dto.onlineLocation?.timezoneId != null),
        externalUrl: dto.meetingType != MeetingType.Other || (dto.startDate == null && dto.endDate == null) || (dto.onlineLocation?.externalUrl != undefined && dto.onlineLocation.externalUrl.length > 0),
        locationName: locationValid,
        meetingRoomId: dto.meetingType != MeetingType.Presential || dto.meetingRoom != null || locationValid
    } as IValidationFields;
}

/////////////////////////////////////////////////////////////////////////////////

const EditMeetingWhenBoxComponent = (props: IPros) => {
    const session = useSession();
    const { mode, visible, readonly = false, onFormUpdate, onFormValidation, viewErrors, viewData } = props;
    const { t } = useTranslation("lms");
    const [formValidation, setFormValidation] = useState<IValidationFields>(getDefaultValidate(viewData, mode));
    const [postValidation, setPostValidation] = useState<IPostValidation>({} as IPostValidation);

    const externalUrlRef = useRef<HTMLInputElement>(undefined!);
    const initialTimeDiff = useRef(viewData.endDate != null && viewData.startDate != null ? viewData.endDate!.getTime() - viewData.startDate!.getTime() : 3600000);

    const meetingLinkRequired = viewData.meetingType == MeetingType.Other && (!viewData.multipleClasses || viewData.startDate != null || viewData.endDate != null);

    const duration = viewData.startDate != null && viewData.endDate != null && viewData.endDate > viewData.startDate
        ? Formaters.performaTimeDiff(viewData.endDate, viewData.startDate)
        : undefined;

    /////////////////////////////////////////////////////////////////////////////////

    ///
    function handleUpdateAllDates(dt: Date, diffMilliseconds: number) {
        const endDate = buildEndDate(dt, diffMilliseconds);

        var newViewData = { ...viewData, startDate: dt, endDate };
        onFormUpdate(newViewData);
        setFormValidation(getDefaultValidate(newViewData, mode));
    }

    ///
    function handleUpdateRecord(name: keyof IData, v: any) {
        var newViewData = { ...viewData, [name]: v };

        if (name === "meetingType") {
            newViewData.onlineLocation = { ...viewData.onlineLocation!, externalUrl: null };
        }
        if (name === "meetingRoom" && v == null) {
            newViewData.location = {} as ManagedMeetingLocationDto;
        }

        onFormUpdate(newViewData);
        setFormValidation(getDefaultValidate(newViewData, mode));
    }

    ///
    const handleUpdateOnlineLocation = (name: keyof ManagedMeetingOnlineLocationDto, v: any) => {
        var newData = viewData.onlineLocation ? { ...viewData.onlineLocation, [name]: v } : { [name]: v } as ManagedMeetingOnlineLocationDto;
        handleUpdateRecord("onlineLocation", newData);
    };

    ///
    const handleUpdateLocation = (name: keyof ManagedMeetingLocationDto, v: any) => {
        var newData = viewData.location ? { ...viewData.location, [name]: v } : { [name]: v } as ManagedMeetingOnlineLocationDto;
        handleUpdateRecord("location", newData);
    };

    ///
    const formatAddress = (address: AddressModel) => {
        var arr = [address.address1, address.address2, address.zipCode, address.city, address.state];
        return arr.filter(text => text).join(", ");
    };

    ///
    const copyLink = (url: string) => {
        HtmlUtils.copyToClipboard(url);
        enqueueSnackbar(t("Student.EnrollmentView.Content.Meeting.CopyLinkSuccessMessage"), { variant: "success" });
    };

    ///
    const syncEndDate = (startDate: Date | null): boolean => {
        if (startDate != null
            && moment(startDate).isValid()
            && !startDate.isInitialOrEndTime()
            && (
                viewData.endDate == null
                || viewData.endDate <= startDate
                || viewData.endDate.isInitialOrEndTime()
            )
        ) {
            handleUpdateAllDates(startDate, initialTimeDiff.current);
            return true;
        }

        return false;
    };

    ///
    const handleChangeStartDate = (date: Date | null, validate: boolean, position: DatePositionChanged, source: MprcDateTimeSource) => {
        if (source == "picker" && validate && syncEndDate(date)) {
            return;
        }
        handleUpdateRecord("startDate", date);
    };

    ///
    const handleChangeStartTimeBlur = () => {
        syncEndDate(viewData.startDate ?? null);
    };

    ///
    const handleChangeEndDate = (date: Date | null, validate: boolean) => {
        handleUpdateRecord("endDate", date);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        onFormValidation(
            Object.values(formValidation).filter((x) => x === false).length == 0,
            Object.values(postValidation).filter(x => x !== undefined).length > 0
        );
    }, [formValidation]);


    useEffect(() => {
        const vErrors = viewErrors ?? {};

        const apiErrors = {
            startDate: vErrors["startDate"],
            endDate: vErrors["endDate"],
            timezoneId: vErrors["timezoneId"] || vErrors["address.TimezoneId"],
            meetingRoomId: vErrors["meetingRoomId"],
            externalUrl: vErrors["externalUrl"],
            locationName: vErrors["locationName"],
        };

        setPostValidation(apiErrors);

        onFormValidation(
            Object.values(formValidation).filter((x) => x === false).length == 0,
            Object.values(apiErrors).filter(x => x !== undefined).length > 0
        );
    }, [viewErrors]);

    ////////////////////////////////////////////////////////////////////////////
    /// INPUT REF'S
    ////////////////////////////////////////////////////////////////////////////

    const onlineMeetingUrl = useMemo(() =>
        viewData.meetingType == MeetingType.Other
            ? viewData.onlineLocation?.externalUrl
            : viewData.meetingType == MeetingType.Teams
                ? viewData.onlineLocation?.teamsUrl
                : viewData.onlineLocation?.zoomUrl
        , [viewData.meetingType, viewData.onlineLocation]);

    useEffect(() => {
        if (externalUrlRef.current) externalUrlRef.current.value = onlineMeetingUrl ?? "";
    }, [externalUrlRef.current, onlineMeetingUrl]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <Box sx={{ display: visible ? "block" : "none" }}>

            <MprcContainer margin="large">
                {
                    mode == "training" && viewData.multipleClasses
                        ? <MprcContainer>
                            <MprcFormSubHeader
                                title={t("Meeting.WhenBox.WhenSubtitle")}
                            />
                            <Typography variant="captionHeader" color="info">
                                {t("Meeting.MainBox.MultipleClassesDateMessage")}
                            </Typography>
                        </MprcContainer>
                        : (
                            <table width="100%">
                                <tbody>
                                    <tr>
                                        <td>
                                            <MprcFormSubHeader
                                                title={t("Meeting.WhenBox.WhenSubtitle")}
                                            />
                                        </td>
                                        <td align="right">
                                            <MprcFormSubHeader
                                                title={t("Meeting.WhenBox.DurationTitle")}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <Grid container spacing={2} alignItems="end">
                                                <Grid>
                                                    <MprcDateTimePicker
                                                        margin="dense"
                                                        disabled={readonly}
                                                        required={mode == "classroom" || !viewData.multipleClasses}
                                                        dateLabel={t("Meeting.WhenBox.StartDateLabel")}
                                                        timeLabel={t("Meeting.WhenBox.DatePickerTimeLabel")}
                                                        shrinkLabel={true}
                                                        value={viewData.startDate}
                                                        format={"initialDate"}
                                                        error={!formValidation.startDate || postValidation.startDate != undefined}
                                                        helperText={postValidation.startDate != undefined && <MprcStringToHtml text={postValidation.startDate} color="error" />}
                                                        onChange={handleChangeStartDate}
                                                        onTimeBlur={handleChangeStartTimeBlur}
                                                    />
                                                </Grid>
                                                <Grid>
                                                    <FontAwesomeIcon icon={faArrowRight} fontSize={"32px"} />
                                                </Grid>
                                                <Grid>
                                                    <MprcDateTimePicker
                                                        margin="dense"
                                                        disabled={readonly}
                                                        required={mode == "classroom" || !viewData.multipleClasses}
                                                        dateLabel={t("Meeting.WhenBox.EndDateLabel")}
                                                        timeLabel={t("Meeting.WhenBox.DatePickerTimeLabel")}
                                                        shrinkLabel={true}
                                                        value={viewData.endDate}
                                                        format={"endDate"}
                                                        minDate={viewData.startDate ?? undefined}
                                                        error={!formValidation.endDate || postValidation.endDate != undefined}
                                                        onChange={handleChangeEndDate}
                                                    />
                                                </Grid>
                                            </Grid>
                                            <FormHelperText>
                                                {postValidation.endDate != undefined
                                                    ? <MprcStringToHtml text={postValidation.endDate} color="error" />
                                                    : <span>&nbsp;</span>
                                                }
                                            </FormHelperText>
                                        </td>
                                        <td align="right">
                                            <Typography variant="body1" component="span">
                                                {duration ?? "---"}
                                            </Typography>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        )
                }

                <Grid container>
                    <Grid size={{ xs: 6 }}>
                        <MprcTimezoneSelect
                            required
                            fullWidth
                            locale={session.user.locale}
                            disabled={readonly || viewData.meetingRoom != null}
                            label={t("Meeting.WhenBox.LocationTimezoneLabel")}
                            value={
                                viewData.meetingRoom != null
                                    ? viewData.meetingRoom.timezoneId
                                    : viewData.meetingType == MeetingType.Presential
                                        ? viewData.location?.timezoneId
                                        : viewData.onlineLocation?.timezoneId ?? ""
                            }
                            onChange={value => viewData.meetingType == MeetingType.Presential ? handleUpdateLocation("timezoneId", value) : handleUpdateOnlineLocation("timezoneId", value)}
                            error={!formValidation.timezoneId || !!postValidation.timezoneId}
                            helperText={!!postValidation.timezoneId && <MprcStringToHtml text={postValidation?.timezoneId} color="error" />}
                        />
                    </Grid>
                </Grid>
            </MprcContainer>

            <MprcContainer>
                <MprcFormSubHeader title={t("Meeting.WhenBox.WhereSubtitle")} margin="dense" />
                {viewData.meetingType == MeetingType.Presential
                    ? <>
                        <MeetingRoomAutoComplete
                            disabled={readonly}
                            required={mode == "classroom" || !viewData.multipleClasses}
                            label={t("Meeting.WhenBox.LocationNameLabel")}
                            startInputValue={viewData.location?.locationName ?? ""}
                            placeholder={t("Meeting.WhenBox.EventRoomPlaceholder")}
                            selectedItems={viewData.meetingRoom ? [viewData.meetingRoom] : []}
                            onChange={items => handleUpdateRecord("meetingRoom", items.length > 0 ? items[0] : null)}
                            onInputValueChange={value => handleUpdateLocation("locationName", value)}
                            error={!!postValidation.locationName || !!postValidation.meetingRoomId || !formValidation.meetingRoomId}
                            helperText={
                                postValidation.locationName != undefined && <MprcStringToHtml text={postValidation?.locationName} /> ||
                                postValidation.meetingRoomId != undefined && !!formValidation.meetingRoomId && <MprcStringToHtml text={postValidation?.meetingRoomId} />
                            }
                        />
                        {viewData.meetingRoom === null
                            ? <EditMeetingAddressBox
                                readonly={readonly}
                                required={false}
                                address={viewData.location?.address ?? getEmptyAddressModel()}
                                onFormUpdate={(address) => handleUpdateLocation("address", address)}
                            />
                            : <>
                                <Typography variant="caption">
                                    {viewData.meetingRoom.address && formatAddress(viewData.meetingRoom.address)}
                                </Typography>
                            </>
                        }
                    </>
                    :
                    <>
                        <RadioGroup row value={viewData.meetingType} onChange={(e, value) => handleUpdateRecord("meetingType", value)}>
                            <MprcRadio
                                value={MeetingType.Teams}
                                label={t("Meeting.WhenBox.EventTeamsLabel")}
                                disabled={readonly || viewData.meetingTypesEnabled.findIndex(x => x == MeetingType.Teams) === -1}
                            />
                            <MprcRadio
                                value={MeetingType.Zoom}
                                label={t("Meeting.WhenBox.EventZoomLabel")}
                                disabled={readonly || viewData.meetingTypesEnabled.findIndex(x => x == MeetingType.Zoom) === -1}
                            />
                            <MprcRadio
                                value={MeetingType.Other}
                                label={`${t("Meeting.WhenBox.EventOtherLabel")} ${meetingLinkRequired ? " *" : ""}`}
                                disabled={readonly || viewData.meetingTypesEnabled.findIndex(x => x == MeetingType.Other) === -1}
                            />
                        </RadioGroup>
                        <Stack direction="row" alignItems="center">
                            <TextField
                                fullWidth
                                required={viewData.meetingType == MeetingType.Other}
                                disabled={readonly || viewData.meetingType != MeetingType.Other}
                                placeholder={
                                    viewData.meetingType != MeetingType.Other
                                        ? t("Meeting.WhenBox.LinkPlaceholder")
                                        : `${t("Meeting.WhenBox.EventOtherLabel")} ${meetingLinkRequired ? " *" : ""}`
                                }
                                inputRef={externalUrlRef}
                                onBlur={e => handleUpdateOnlineLocation("externalUrl", externalUrlRef.current.value)}
                                error={!formValidation.externalUrl || !!postValidation?.externalUrl}
                                helperText={<MprcStringToHtml text={postValidation?.externalUrl} color="error" />}
                            />
                            <IconButton
                                disabled={onlineMeetingUrl == null || onlineMeetingUrl.length == 0}
                                title={t("Meeting.WhenBox.CopyLinkTooltip")} size="small" onClick={() => copyLink(viewData.onlineLocation?.externalUrl || "")}>
                                <IconCopy size="sm" />
                            </IconButton>
                        </Stack>
                    </>
                }
            </MprcContainer>
        </Box>
    );
};

export const EditMeetingWhenBox = React.memo(EditMeetingWhenBoxComponent, (prev, next) => {
    return LibUtils.deepCompare(prev.viewData, next.viewData)
        && prev.mode == next.mode
        && prev.visible == next.visible
        && prev.readonly == next.readonly
        && prev.viewErrors == next.viewErrors
        ;
});
